import React, { useState, useEffect } from 'react';
import supabase from './supabase/supabase';
import { useProducts } from './hooks/useProducts';
import ProductTable from './components/ProductTable';
import ProductForm from './components/ProductForm';
import SupplierManagement from './components/SupplierManagement';
import ProfileManagement from './components/ProfileManagement';
import Auth from './components/Auth';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiBox, FiLogOut, FiUser, FiPackage, FiTruck, FiUsers } = FiIcons;

function MainInventory({ user }) {
  const {
    products,
    loading,
    addProduct,
    updateProduct,
    deleteProduct,
    suppliers,
    loadingSuppliers,
    addSupplier,
    updateSupplier,
    deleteSupplier
  } = useProducts();

  const [activeTab, setActiveTab] = useState('products');
  const [productView, setProductView] = useState('table');
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Estado para el rol en tiempo real
  const [dbRole, setDbRole] = useState('viewer');

  const fetchRealRole = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();
      
    if (data) {
      setDbRole(data.role);
    }
  };

  useEffect(() => {
    fetchRealRole();
  }, [user]);

  // Variables calculadas basadas en el rol real de la base de datos
  const isAdmin = dbRole === 'admin';
  const canEdit = dbRole === 'admin' || dbRole === 'editor';

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductView('add');
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductView('edit');
  };

  const handleSaveProduct = async (productData) => {
    if (productView === 'edit' && editingProduct) {
      await updateProduct(editingProduct.id, productData);
    } else {
      await addProduct(productData);
    }
    setProductView('table');
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este producto?')) {
      await deleteProduct(id);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const handleProfileUpdated = async () => {
    // Actualizamos el rol localmente sin necesidad de cerrar sesión
    await fetchRealRole();
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="bg-indigo-600 p-2 rounded-lg mr-3">
                <SafeIcon icon={FiBox} className="text-white text-xl" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Inventario</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              {user?.email && (
                <div className="hidden sm:flex items-center text-sm text-gray-600 bg-gray-100 pl-3 pr-1 py-1 rounded-full border border-gray-200">
                  <SafeIcon icon={FiUser} className="mr-2 text-gray-400" />
                  <span className="font-medium mr-3">{user.email}</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full uppercase font-bold tracking-wider 
                    ${isAdmin ? 'bg-indigo-200 text-indigo-800' : 
                      dbRole === 'editor' ? 'bg-green-200 text-green-800' : 
                      'bg-gray-200 text-gray-800'}`}>
                    {dbRole}
                  </span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center text-sm text-gray-600 hover:text-red-600 transition-colors p-2 rounded-lg hover:bg-red-50"
                title="Cerrar Sesión"
              >
                <SafeIcon icon={FiLogOut} className="sm:mr-2 text-lg sm:text-base" />
                <span className="hidden sm:inline font-medium">Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Sistema de Pestañas */}
        <div className="flex border-b border-gray-200 mb-8 space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('products')}
            className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors flex items-center whitespace-nowrap ${
              activeTab === 'products'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <SafeIcon icon={FiPackage} className="mr-2 text-lg" />
            Productos
          </button>
          <button
            onClick={() => setActiveTab('suppliers')}
            className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors flex items-center whitespace-nowrap ${
              activeTab === 'suppliers'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <SafeIcon icon={FiTruck} className="mr-2 text-lg" />
            Proveedores
          </button>
          {/* Pestaña de Usuarios (Solo Admin) */}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('profiles')}
              className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors flex items-center whitespace-nowrap ${
                activeTab === 'profiles'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SafeIcon icon={FiUsers} className="mr-2 text-lg" />
              Usuarios
            </button>
          )}
        </div>

        {/* Contenido principal */}
        {activeTab === 'products' ? (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {productView === 'table' ? 'Gestión de Productos' : (productView === 'add' ? 'Agregar Producto' : 'Editar Producto')}
                </h2>
                <p className="text-gray-500 mt-1">
                  Administra tu catálogo, precios y niveles de stock.
                </p>
              </div>
              {/* Ocultar botón Agregar a Viewers */}
              {canEdit && productView === 'table' && (
                <button
                  onClick={handleAddProduct}
                  className="flex items-center px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <SafeIcon icon={FiPlus} className="mr-2 text-lg" />
                  Agregar Producto
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
              </div>
            ) : productView === 'table' ? (
              <ProductTable
                products={products}
                suppliers={suppliers}
                onEdit={handleEditProduct}
                onDelete={handleDeleteProduct}
                isAdmin={isAdmin}
                canEdit={canEdit}
              />
            ) : (
              <ProductForm
                initialData={editingProduct}
                suppliers={suppliers}
                onSave={handleSaveProduct}
                onCancel={() => setProductView('table')}
              />
            )}
          </div>
        ) : activeTab === 'suppliers' ? (
          <SupplierManagement
            suppliers={suppliers}
            loading={loadingSuppliers}
            onAdd={addSupplier}
            onUpdate={updateSupplier}
            onDelete={deleteSupplier}
            isAdmin={isAdmin}
            canEdit={canEdit}
          />
        ) : activeTab === 'profiles' && isAdmin ? (
          <ProfileManagement 
            currentUser={user} 
            onProfileUpdated={handleProfileUpdated} 
          />
        ) : null}
      </main>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsSessionLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (isSessionLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!session) {
    return <Auth />;
  }

  return <MainInventory user={session.user} />;
}